"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import type { DashboardStats } from "@/types";
import { Header } from "@/components/layout/header";
import { cn, formatCurrency, formatShortDate } from "@/lib/utils";
import {
  ShoppingCart, TrendingUp, Users, Package,
  Clock,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";

const DASH_HEX_DEFAULTS: Record<string, string> = {
  siparis: "#0ea5e9", ciro: "#10b981", bekleyen: "#f59e0b",
  islemdeki: "#8b5cf6", musteri: "#8b5cf6", urun: "#64748b",
};

export default function DashboardPage() {
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ["dashboard-stats"],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/dashboard/stats");
      return data;
    },
    refetchInterval: 60_000,
  });

  const { data: adminSettings } = useQuery<Record<string, string>>({
    queryKey: ["settings", "admin"],
    queryFn: async () => { const { data } = await api.get("/api/admin/settings/admin"); return data; },
    staleTime: 5 * 60 * 1000,
  });

  const dc = {
    siparis:   adminSettings?.["admin.dash_siparis"]   ?? DASH_HEX_DEFAULTS.siparis,
    ciro:      adminSettings?.["admin.dash_ciro"]      ?? DASH_HEX_DEFAULTS.ciro,
    bekleyen:  adminSettings?.["admin.dash_bekleyen"]  ?? DASH_HEX_DEFAULTS.bekleyen,
    islemdeki: adminSettings?.["admin.dash_islemdeki"] ?? DASH_HEX_DEFAULTS.islemdeki,
    musteri:   adminSettings?.["admin.dash_musteri"]   ?? DASH_HEX_DEFAULTS.musteri,
    urun:      adminSettings?.["admin.dash_urun"]      ?? DASH_HEX_DEFAULTS.urun,
  };

  const salesChartData = stats?.last7Days.map((day) => ({
    date: formatShortDate(day.date),
    ciro: day.revenue,
    siparis: day.orderCount,
  })) ?? [];

  const revenueSeries = stats?.last7Days.map((day) => day.revenue) ?? [];
  const orderSeries = stats?.last7Days.map((day) => day.orderCount) ?? [];
  const totalRevenueLast7Days = sumValues(revenueSeries);
  const totalOrdersLast7Days = sumValues(orderSeries);
  const totalOrders = stats?.totalOrderCount ?? 0;
  const totalCustomers = stats?.totalCustomerCount ?? 0;
  const totalProducts = stats?.totalProductCount ?? 0;
  const last7DayCount = Math.max(stats?.last7Days.length ?? 0, 1);
  const averageDailyRevenue = totalRevenueLast7Days / last7DayCount;
  const averageDailyOrders = totalOrdersLast7Days / last7DayCount;
  const averageOrderValue = totalRevenueLast7Days / Math.max(totalOrdersLast7Days, 1);
  const yesterdayRevenue = revenueSeries.at(-2) ?? 0;
  const yesterdayOrders = orderSeries.at(-2) ?? 0;
  const revenueMomentum = percentageChange(yesterdayRevenue, stats?.todayRevenue ?? 0);
  const orderMomentum = percentageChange(yesterdayOrders, stats?.todayOrderCount ?? 0);
  const activePipeline = Math.max((stats?.pendingOrderCount ?? 0) + (stats?.processingOrderCount ?? 0), 1);
  const healthyProductCount = Math.max(
    totalProducts - (stats?.lowStockProductCount ?? 0) - (stats?.outOfStockProductCount ?? 0),
    0,
  );
  const healthyStockRatio = totalProducts > 0
    ? (healthyProductCount / totalProducts) * 100
    : 0;
  const customerCoverageRatio = totalOrders > 0
    ? (totalCustomers / totalOrders) * 100
    : 0;
  const todayRevenueShare = totalRevenueLast7Days > 0
    ? ((stats?.todayRevenue ?? 0) / totalRevenueLast7Days) * 100
    : 0;
  const todayOrderShare = totalOrdersLast7Days > 0
    ? ((stats?.todayOrderCount ?? 0) / totalOrdersLast7Days) * 100
    : 0;
  return (
    <>
      <Header title="Dashboard" />
      <main className="flex-1 overflow-y-auto pt-2 sm:pt-3 pb-4 sm:pb-6 px-4 sm:px-6">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-24 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : stats ? (
          <div className="space-y-5">

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: "easeOut" }}>
                <StatCard
                  title="Sipariş"
                  value={stats.todayOrderCount.toString()}
                  icon={ShoppingCart}
                  color={dc.siparis}
                  delay={0.02}
                  insight={`${averageDailyOrders.toFixed(1)} ort / gün`}
                  trendText={`${formatSignedPercent(orderMomentum)} dünden`}
                  visual={{ kind: "sparkline", data: orderSeries, chartId: "today-orders" }}
                  glow="glow-sky"
                  badge="Bugün"
                />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: "easeOut" }}>
                <StatCard
                  title="Ciro"
                  value={formatCurrency(stats.todayRevenue)}
                  icon={TrendingUp}
                  color={dc.ciro}
                  delay={0.08}
                  insight={`7 günde ${formatCurrency(totalRevenueLast7Days)}`}
                  trendText={`${formatSignedPercent(revenueMomentum)} dünden`}
                  visual={{ kind: "sparkline", data: revenueSeries, chartId: "today-revenue" }}
                  glow="glow-emerald"
                  badge="Bugün"
                />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: "easeOut" }}>
                <StatCard
                  title="Bekleyen"
                  value={stats.pendingOrderCount.toString()}
                  icon={Clock}
                  color={dc.bekleyen}
                  delay={0.14}
                  insight={`Aktif akışta ${formatPercent((stats.pendingOrderCount / activePipeline) * 100)} pay`}
                  trendText="İşlem öncesi kuyruk"
                  visual={{
                    kind: "progress",
                    value: stats.pendingOrderCount,
                    total: activePipeline,
                    label: "Aktif sipariş akışı",
                  }}
                  glow="glow-amber"
                  badge="Bugün"
                />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: "easeOut" }}>
                <StatCard
                  title="İşlemdeki"
                  value={stats.processingOrderCount.toString()}
                  icon={Package}
                  color={dc.islemdeki}
                  delay={0.2}
                  insight={`Aktif akışta ${formatPercent((stats.processingOrderCount / activePipeline) * 100)} pay`}
                  trendText="Hazırlanan siparişler"
                  visual={{
                    kind: "progress",
                    value: stats.processingOrderCount,
                    total: activePipeline,
                    label: "Aktif sipariş akışı",
                  }}
                  glow="glow-violet"
                  badge="Bugün"
                />
              </motion.div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: "easeOut" }}>
                <StatCard
                  title="Sipariş"
                  value={stats.totalOrderCount.toLocaleString("tr")}
                  icon={ShoppingCart}
                  color={dc.siparis}
                  delay={0.04}
                  insight={`Bugün payı ${formatPercent(todayOrderShare)}`}
                  trendText={`7 gün ${totalOrdersLast7Days.toLocaleString("tr")} adet`}
                  visual={{
                    kind: "progress",
                    value: stats.todayOrderCount,
                    total: Math.max(totalOrdersLast7Days, 1),
                    label: "7 günlük sipariş payı",
                  }}
                  glow="glow-sky"
                  badge="Toplam"
                />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: "easeOut" }}>
                <StatCard
                  title="Ciro"
                  value={formatCurrency(stats.totalRevenue)}
                  icon={TrendingUp}
                  color={dc.ciro}
                  delay={0.1}
                  insight={`Bugün payı ${formatPercent(todayRevenueShare)}`}
                  trendText={`Ort. sepet ${formatCurrency(averageOrderValue)}`}
                  visual={{
                    kind: "progress",
                    value: stats.todayRevenue,
                    total: Math.max(totalRevenueLast7Days, 1),
                    label: "7 günlük ciro payı",
                  }}
                  glow="glow-emerald"
                  badge="Toplam"
                />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: "easeOut" }}>
                <StatCard
                  title="Müşteri"
                  value={stats.totalCustomerCount.toLocaleString("tr")}
                  icon={Users}
                  color={dc.musteri}
                  delay={0.16}
                  insight={`Müşteri kapsaması ${formatPercent(customerCoverageRatio)}`}
                  trendText="Toplam siparişe oran"
                  visual={{
                    kind: "progress",
                    value: stats.totalCustomerCount,
                    total: Math.max(totalOrders, 1),
                    label: "Sipariş bazlı müşteri kapsaması",
                  }}
                  glow="glow-violet"
                  badge="Toplam"
                />
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: "easeOut" }}>
                <StatCard
                  title="Ürün"
                  value={stats.totalProductCount.toLocaleString("tr")}
                  icon={Package}
                  color={dc.urun}
                  delay={0.22}
                  insight={`Sağlıklı stok ${formatPercent(healthyStockRatio)}`}
                  trendText={`${healthyProductCount.toLocaleString("tr")} ürün güvenli aralıkta`}
                  visual={{
                    kind: "progress",
                    value: healthyProductCount,
                    total: Math.max(totalProducts, 1),
                    label: "Stok sağlığı",
                  }}
                  badge="Toplam"
                />
              </motion.div>
            </div>

            <section className="grid gap-4 xl:grid-cols-[minmax(0,1.55fr)_minmax(320px,0.95fr)]">
              <motion.div
                className="admin-panel rounded-xl overflow-hidden"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.12, ease: "easeOut" }}
              >
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Son 7 Günlük Satış</p>
                    {salesChartData.length > 0 && (
                      <p className="text-xl font-bold text-primary mt-0.5">
                        {formatCurrency(totalRevenueLast7Days)}
                      </p>
                    )}
                    <p className="text-sm text-slate-500 mt-1">
                      Günlük ortalama {formatCurrency(averageDailyRevenue)} ciro ve {averageDailyOrders.toFixed(1)} sipariş
                    </p>
                  </div>
                  <span className="flex items-center gap-1.5 text-xs text-slate-400">
                    <span className="inline-block h-2 w-2 rounded-full bg-sky-400" />
                    Ciro akışı
                  </span>
                </div>
                <div className="px-2 py-4">
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart
                      data={salesChartData}
                      margin={{ top: 4, right: 20, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorCiro" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.30} />
                          <stop offset="60%" stopColor="#60a5fa" stopOpacity={0.10} />
                          <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#64748b" }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                        width={36}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "12px",
                          border: "1px solid rgba(148,163,184,0.20)",
                          backgroundColor: "#1e293b",
                          fontSize: "12px",
                          color: "#e2e8f0",
                          boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
                          padding: "10px 14px",
                        }}
                        formatter={(value: number, name: string) =>
                          name === "ciro" ? [formatCurrency(value), "Ciro"] : [value, "Sipariş"]
                        }
                      />
                      <Area
                        type="monotone"
                        dataKey="ciro"
                        stroke="#60a5fa"
                        strokeWidth={2.5}
                        fill="url(#colorCiro)"
                        dot={false}
                        activeDot={{ r: 5, strokeWidth: 0, fill: "#93c5fd" }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>

              <motion.div
                className="admin-panel rounded-xl overflow-hidden h-full"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.15, ease: "easeOut" }}
              >
                <PaymentChannelsChart />
              </motion.div>
            </section>

          </div>
        ) : null}
      </main>
    </>
  );
}

type StatVisual =
  | { kind: "sparkline"; data: number[]; chartId: string }
  | { kind: "progress"; value: number; total: number; label: string };

function StatCard({
  title, value, icon: Icon, color, insight, trendText, visual, delay, glow, badge,
}: {
  title: string;
  value: string;
  icon: React.ElementType;
  color: string;
  insight: string;
  trendText: string;
  visual: StatVisual;
  delay: number;
  glow?: string;
  badge?: string;
}) {
  const fill = color.startsWith("#") ? color : ({
    sky:"#0ea5e9", emerald:"#10b981", amber:"#f59e0b", violet:"#8b5cf6",
    slate:"#64748b", rose:"#f43f5e", blue:"#3b82f6", orange:"#f97316",
    teal:"#14b8a6", indigo:"#6366f1", purple:"#a855f7", pink:"#ec4899",
  } as Record<string,string>)[color] ?? "#64748b";
  const progressRatio = visual.kind === "progress"
    ? Math.min(100, (visual.value / Math.max(visual.total, 1)) * 100)
    : 0;

  return (
    <motion.div
      className={cn("admin-panel rounded-xl", glow)}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay, ease: "easeOut" }}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{title}</p>
              {badge && (
                <span className="text-[9px] font-semibold text-slate-500 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded uppercase tracking-wider">
                  {badge}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-primary tracking-tight leading-none">{value}</p>
            <p className="text-xs text-slate-500 mt-3">{insight}</p>
          </div>
          <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
               style={{ backgroundColor: `${fill}15`, border: `1px solid ${fill}30` }}>
            <Icon className="h-5 w-5 shrink-0" style={{ color: fill }} />
          </div>
        </div>

        <div className="mt-4">
          {visual.kind === "sparkline" ? (
            <div className="h-14">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={visual.data.map((point, index) => ({ index, value: point }))}
                  margin={{ top: 4, right: 0, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id={`${visual.chartId}-fill`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={fill} stopOpacity={0.28} />
                      <stop offset="70%" stopColor={fill} stopOpacity={0.08} />
                      <stop offset="100%" stopColor={fill} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={fill}
                    strokeWidth={2}
                    fill={`url(#${visual.chartId}-fill)`}
                    dot={false}
                    isAnimationActive
                    animationDuration={900}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between text-[11px] text-slate-500 mb-2">
                <span>{visual.label}</span>
                <span>{formatPercent(progressRatio)}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-700/50 overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: fill }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressRatio}%` }}
                  transition={{ duration: 0.8, delay: delay + 0.1, ease: "easeOut" }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-3 text-xs font-medium text-slate-500">{trendText}</div>
      </div>
    </motion.div>
  );
}

const PAYMENT_CHANNELS = [
  { name: "Kredi Kartı", value: 45, color: "#6366f1", gradient: ["#818cf8", "#4f46e5"] },
  { name: "Havale/EFT", value: 20, color: "#10b981", gradient: ["#34d399", "#059669"] },
  { name: "Kapıda Ödeme", value: 15, color: "#f59e0b", gradient: ["#fbbf24", "#d97706"] },
  { name: "iyzico", value: 10, color: "#0ea5e9", gradient: ["#38bdf8", "#0284c7"] },
  { name: "PayTR", value: 7, color: "#8b5cf6", gradient: ["#a78bfa", "#7c3aed"] },
  { name: "Stripe", value: 3, color: "#ec4899", gradient: ["#f472b6", "#db2777"] },
];

function PaymentChannelsChart() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const total = PAYMENT_CHANNELS.reduce((s, c) => s + c.value, 0);

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Ödeme Kanalları</p>
          <p className="text-sm text-slate-500 mt-1">Son 7 gün sipariş dağılımı</p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <span className="inline-block h-2 w-2 rounded-full bg-indigo-400" />
          <span>6 kanal</span>
        </div>
      </div>

      <div className="flex-1 px-4 py-4 flex items-center gap-4">
        <div className="relative flex-1 h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                {PAYMENT_CHANNELS.map((c, i) => (
                  <linearGradient key={i} id={`pieGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={c.gradient[0]} />
                    <stop offset="100%" stopColor={c.gradient[1]} />
                  </linearGradient>
                ))}
              </defs>
              <Pie
                data={PAYMENT_CHANNELS}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={85}
                paddingAngle={3}
                dataKey="value"
                animationBegin={200}
                animationDuration={900}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {PAYMENT_CHANNELS.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`url(#pieGrad${index})`}
                    stroke="transparent"
                    style={{
                      filter: activeIndex === index ? `drop-shadow(0 0 10px ${entry.color}66)` : "none",
                      transform: activeIndex === index ? "scale(1.08)" : "scale(1)",
                      transformOrigin: "center",
                      transition: "all 0.3s ease",
                    }}
                  />
                ))}
              </Pie>
              <Tooltip content={<PaymentTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Center label */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <p className="text-2xl font-bold text-primary tracking-tight">{total}%</p>
            <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mt-0.5">Toplam</p>
          </div>
        </div>

        {/* Legend */}
        <div className="w-28 space-y-1.5">
          {PAYMENT_CHANNELS.map((c, i) => (
            <div
              key={c.name}
              className="flex items-center gap-2 cursor-pointer"
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0 ring-2 ring-white dark:ring-slate-900"
                style={{ backgroundColor: c.color }}
              />
              <div className="min-w-0">
                <p className={cn(
                  "text-[11px] font-medium truncate transition-colors",
                  activeIndex === i ? "text-primary" : "text-slate-600 dark:text-slate-300"
                )}>
                  {c.name}
                </p>
                <p className="text-[10px] text-slate-400 font-semibold">{c.value}%</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PaymentTooltip({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number; payload: { color: string } }> }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="rounded-xl border border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700 px-3 py-2 shadow-lg">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.payload.color }} />
        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{p.name}</span>
      </div>
      <p className="text-[11px] text-slate-500 mt-1">{p.value}% pay</p>
    </div>
  );
}

function sumValues(values: number[]) {
  return values.reduce((sum, value) => sum + value, 0);
}

function percentageChange(previous: number, current: number) {
  if (previous <= 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

function formatPercent(value: number) {
  return `${Math.round(value)}%`;
}

function formatSignedPercent(value: number) {
  if (value === 0) return "0%";
  const rounded = Math.round(value);
  return `${rounded > 0 ? "+" : ""}${rounded}%`;
}
